import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboard } from '../api/client';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await dashboard.get();
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  const { recent_recipes, upcoming_meals, active_shopping_lists, stats } = data || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Recipes" value={stats?.total_recipes || 0} icon="📖" />
        <StatCard label="Categories" value={stats?.total_categories || 0} icon="🏷️" />
        <StatCard label="Meals Planned" value={upcoming_meals?.length || 0} icon="📅" />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <QuickAction to="/app/recipes?new=true" label="Add Recipe" icon="➕" />
        <QuickAction to="/app/meals" label="Plan Meals" icon="📅" />
        <QuickAction to="/app/shopping" label="Shopping Lists" icon="🛒" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Recipes */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Recipes</h2>
            <Link to="/app/recipes" className="text-sm text-indigo-600 hover:text-indigo-500">
              View all
            </Link>
          </div>
          {recent_recipes?.length > 0 ? (
            <ul className="space-y-3">
              {recent_recipes.map((recipe) => (
                <li key={recipe.id}>
                  <Link
                    to={`/app/recipes/${recipe.id}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{recipe.title}</p>
                      {recipe.total_time_minutes > 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {recipe.total_time_minutes} min
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {recipe.categories?.slice(0, 2).map((cat) => (
                        <span
                          key={cat.id}
                          className="px-2 py-1 text-xs rounded-full"
                          style={{ backgroundColor: cat.color + '20', color: cat.color }}
                        >
                          {cat.name}
                        </span>
                      ))}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              No recipes yet.{' '}
              <Link to="/app/recipes?new=true" className="text-indigo-600 hover:underline">
                Add your first recipe
              </Link>
            </p>
          )}
        </div>

        {/* Upcoming Meals */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upcoming Meals</h2>
            <Link to="/app/meals" className="text-sm text-indigo-600 hover:text-indigo-500">
              View calendar
            </Link>
          </div>
          {upcoming_meals?.length > 0 ? (
            <ul className="space-y-3">
              {upcoming_meals.map((meal) => (
                <li key={meal.id} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {formatDate(meal.meal_date)}
                        {meal.meal_type && ` - ${capitalize(meal.meal_type)}`}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {meal.recipes.map((r) => r.title).join(', ')}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              No meals planned.{' '}
              <Link to="/app/meals" className="text-indigo-600 hover:underline">
                Plan your meals
              </Link>
            </p>
          )}
        </div>
      </div>

      {/* Active Shopping Lists */}
      {active_shopping_lists?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Active Shopping Lists</h2>
            <Link to="/app/shopping" className="text-sm text-indigo-600 hover:text-indigo-500">
              View all
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {active_shopping_lists.map((list) => (
              <Link
                key={list.id}
                to={`/app/shopping?list=${list.id}`}
                className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-indigo-300 transition-colors"
              >
                <p className="font-medium text-gray-900 dark:text-white">{list.name}</p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-green-500 rounded-full h-2 transition-all"
                      style={{ width: `${list.progress_percent}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {list.checked_count}/{list.items_count}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <div className="flex items-center gap-3">
        <span className="text-2xl">{icon}</span>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  );
}

function QuickAction({ to, label, icon }) {
  return (
    <Link
      to={to}
      className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 hover:shadow-md transition-shadow flex items-center gap-3"
    >
      <span className="text-2xl">{icon}</span>
      <span className="font-medium text-gray-900 dark:text-white">{label}</span>
    </Link>
  );
}

function formatDate(dateString) {
  const date = new Date(dateString + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.getTime() === today.getTime()) return 'Today';
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';

  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
